/* global THREE */

import aframe from 'aframe';
import * as ticker from '@micosmo/ticker/aframe-ticker';
import { onLoadedDo } from '@micosmo/aframe/startup';
import { bindEvent } from 'aframe-event-decorators/event-binder';
import { isVisibleInScene } from '@micosmo/aframe/lib/utils';

var flHMDDetected = undefined; // Require 3 checkable states.
var cursorEl, cameraEl, targetEl, triggerEl;
var cursorCameraPosition, saveCameraPosition;
var lastRaycasterData = {};
var triggerCache;
var triggerCacheTimeout = 0;
var hc;

aframe.registerComponent("headless-controller", {
  init() {
    hc = this;
    this.el.sceneEl.systems.keyboard.addListeners(this);
    cursorEl = this.el;
    cameraEl = document.getElementById('player-camera');
    onLoadedDo(() => {
      headlessReady();
      cursorCameraPosition = cameraEl.object3D.position.clone();
      // Adjust the camera for cursor input
      cursorCameraPosition.z -= 0.4;
      cursorCameraPosition.y -= -0.1;
    });
    this.triggerTimer = ticker.createProcess(ticker.msWaiter(100, triggerUp.bind(this)));
    this.triggerCacheTimeout = ticker.createProcess(fTriggerCacheTimeout.bind(this));
  },
  hasHMD() {
    return flHMDDetected;
  },
  gamestatechanged: bindEvent({ target: '[game-state]' }, function (evt) {
    const newState = evt.detail;
    switch (newState) {
    case 'MainMenu' :
      triggerCacheTimeout = 0;
      cursorEl.setAttribute('raycaster', {
        objects: '.cursor-menu',
        interval: 125
      });
      break;
    case 'Playing' :
      triggerCacheTimeout = 1000;
      cursorEl.setAttribute('raycaster', {
        objects: '[headless], #timeWarpButton',
        interval: 0
      });
      break;
    case 'GameOver':
      triggerCacheTimeout = 0;
      cursorEl.setAttribute('raycaster', {
        objects: '.cursor-gameover',
        interval: 125
      });
      break;
    default:
      triggerCacheTimeout = 0;
      cursorEl.setAttribute('raycaster', {
        objects: '.cursor-none',
        interval: 10000
      });
      break;
    }
  }),
  pauseStarted: bindEvent({ target: '[game-state]' }, function (evt) {
    Object.assign(lastRaycasterData, cursorEl.components.raycaster.data);
    cursorEl.setAttribute('raycaster', {
      objects: '.cursor-paused',
      interval: 500
    });
  }),
  pauseEnded: bindEvent({ target: '[game-state]' }, function (evt) {
    cursorEl.setAttribute('raycaster', {
      objects: lastRaycasterData.objects,
      interval: lastRaycasterData.interval
    });
  }),
  toggleCursor() {
    if (cursorEl.components.raycaster.data.enabled) {
      cursorEl.object3D.visible = false;
      cameraEl.object3D.position.copy(saveCameraPosition);
      cursorEl.setAttribute('raycaster', 'enabled', 'false');
    } else {
      cursorEl.object3D.visible = true;
      saveCameraPosition = cameraEl.object3D.position.clone();
      cameraEl.object3D.position.copy(cursorCameraPosition);
      cursorEl.setAttribute('raycaster', 'enabled', 'true');
    }
  },

  mouseenter: bindEvent(function (evt) {
    targetSelected.call(this, evt);
  }),
  mouseleave: bindEvent(function (evt) {
    targetDeselected.call(this, evt);
  }),
  click: bindEvent(function (evt) {
    this.keydown_Trigger();
  }),
  keydown_Trigger() {
    triggerDown.call(this, 'Trigger');
    return true;
  },
  keydown_Omega() {
    triggerDown.call(this, 'Omega');
    return true;
  }
});

function targetSelected(evt) {
  const el = evt.detail.intersectedEl;
  if (isVisibleInScene(el)) {
    targetEl = el;
    this.el.setAttribute('material', 'color', 'green');
    tryTriggerCache.call(this);
  }
}

function targetDeselected() {
  if (targetEl) {
    tryTriggerCache.call(this);
    targetEl = undefined;
  }
  this.el.setAttribute('material', 'color', 'red');
}

function tryTriggerCache() {
  if (!triggerCache)
    return;
  emitTriggerDown.call(this, targetEl, triggerCache);
  triggerCache = undefined;
  this.triggerCacheTimeout.stop();
}

function triggerDown(key) {
  if (triggerEl)
    return;
  if (targetEl) {
    emitTriggerDown.call(this, targetEl, key);
  } else if (triggerCacheTimeout > 0) {
    if (!triggerCache)
      this.triggerCacheTimeout.start();
    triggerCache = key;
  }
}

function emitTriggerDown(el, key) {
  (triggerEl = el).emit('triggerDown', {
    triggerEl,
    key
  });
  this.triggerTimer.start();
}

function triggerUp() {
  if (triggerEl)
    triggerEl.emit('triggerUp');
  triggerEl = undefined;
}

function * fTriggerCacheTimeout() {
  yield ticker.msWaiter(triggerCacheTimeout);
  triggerCache = undefined;
}

function headlessReady() {
  if (aframe.utils.device.checkHeadsetConnected()) {
    flHMDDetected = true;
    console.info(`system:headless-controller:headlessReady: HMD device detected. Headless controller ready`);
    return;
  }
  console.info(`system:headless-controller:headlessReady: No HMD device. Headless controller ready`);
}

const v1 = new THREE.Vector3();
const v2 = new THREE.Vector3();

aframe.registerComponent("headless", {
  init() {
    this.gameStateEl = document.querySelector('[game-state]');
    this.omegaSilo = document.querySelector("#omega-missile-silo");
  },
  triggerDown: bindEvent(function (evt) {
    const components = this.el.components;
    if (components.missile)
      this.targetMissile(evt.detail.key, components.missile);
    else if (components['smart-missile'])
      this.targetSmartMissile(evt.detail.key, components['smart-missile']);
    else if (components.bomber)
      this.targetBomber(evt.detail.key, components.bomber);
  }),

  targetMissile(key, missile) {
    this.el.object3D.getWorldPosition(v1);
    v1.addScaledVector(this.el.object3D.getWorldDirection(v2).negate(), 1 * missile.data.speed * missile._timeWarpFactor);
    this.fireAt(key, v1);
  },
  targetSmartMissile(key, smartMissile) {
    this.el.object3D.getWorldPosition(v1);
    v1.addScaledVector(this.el.object3D.getWorldDirection(v2).negate(), 1 * smartMissile.data.speed * smartMissile._timeWarpFactor);
    this.fireAt(key, v1);
  },
  targetBomber(key, bomber) {
    this.el.object3D.getWorldPosition(v1);
    v1.addScaledVector(this.el.object3D.getWorldDirection(v2).negate(), 1 * bomber.data.speed * bomber._timeWarpFactor);
    this.fireAt(key, v1);
  },

  fireAt(key, v) {
    const isAdvancedControls = this.gameStateEl.components["game-state"].isAdvancedControls();
    if (key === 'Trigger')
      this.launch(v, isAdvancedControls);
    else if (isAdvancedControls)
      this.launchOmega(v);
  },

  launch(v, deltaOnly = false) {
    const silo = this.getNearestSilo(v, deltaOnly);
    if (silo !== undefined && silo.components["missile-silo"].canFire()) {
      silo.components["missile-silo"].fireAt(v);
    } else
      hc.el.components.misound__noammo.playSound();
  },

  launchOmega(v) {
    const silo = this.omegaSilo;
    if (silo !== undefined && silo.components["missile-silo"].canFire()) {
      silo.components["missile-silo"].fireAt(v);
    } else
      hc.el.components.misound__noammo.playSound();
  },

  getNearestSilo(v, deltaOnly = false) {
    const missileSilos = document.querySelectorAll(".missile-silo");
    let nearest = undefined;
    let nearestDistance = -1;
    missileSilos.forEach(silo => {
      if (!silo.components["missile-silo"].canFire() || (deltaOnly && silo.id === "omega-missile-silo"))
        return;
      const d = v.distanceTo(silo.object3D.getWorldPosition(v2));
      if (nearest === undefined || d < nearestDistance) {
        nearest = silo;
        nearestDistance = d;
      }
    });
    return nearest;
  }
});
